import React, { useRef, useEffect, useState, useCallback } from 'react'
import { LMS_ADMIN_WRITE, SUPER_ADMIN_DELETE } from '../../../../utils/roles'
import KeycloakService from '../../../../utils/keycloakService'
import '../../../administration/admin.scss'
import MedAgDataGrid from '../../../../components/MedAgDataGrid'
import { ColDef, INumberFilterParams } from 'ag-grid-community'
import '../../../lms/lms.scss'
import { IconButton, useTheme } from '@mui/material'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  selectedLmsFormMode,
  setLmsFormMode,
  setLmsItemId,
} from '../../lmsSlice'
import { toast } from 'react-toastify'
import {
  enableDeviceModel,
  getAllLabDeviceModels,
} from '../../../../services/LmsService'
import {
  CREATE,
  LAB_ANALYZER_TABLE_HEADING,
  lmsFormCreateMode,
  lmsFormEditMode,
  lmsFormNoneMode,
  MODEL,
  NAVIGATE_PARAMETER_MAPPING,
} from '../../constants'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import { useNavigate } from 'react-router-dom'
import CreateUpdateLabDeviceModel from './CreateUpdateLabDeviceModel'
import { readableDateTimeFormat } from '../../../../utils/dateUtils'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import CustomHeader from '../../../Radiology/components/RadiologyModel/CustomHeader'
import { UnarchiveOutlined } from '@mui/icons-material'
import { messages } from '../../../administration/constants'
import { active } from '../../models'
import {
  activeText,
  inactiveText,
} from '../../../Radiology/Constants/constants'

export const styles = () => ({
  columnFontSize: {
    fontSize: '12px',
  },
  enableColumnStyle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
})

export const LabDeviceModel = () => {
  const depTableContainerRef = useRef<HTMLDivElement | null>(null)
  const dispatch = useAppDispatch()
  const mode = useAppSelector(selectedLmsFormMode)
  const [data, setData] = useState([])
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(100)
  const [totalCount, setTotalCount] = useState(0)
  const [selectItem, setSelectItem] = useState<any>({})
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [archived, setArchived] = useState(false)

  useEffect(() => {
    const resizeHandler = () => {
      const container = depTableContainerRef?.current
      if (container) {
        const availableHeight: any =
          window?.innerHeight - container?.getBoundingClientRect()?.top
        container.style.maxHeight = `${availableHeight - 20}px`
      }
    }

    window?.addEventListener('resize', resizeHandler)
    resizeHandler()

    return () => {
      window?.removeEventListener('resize', resizeHandler)
    }
  }, [])

  async function onEdit(id: any) {
    dispatch(setLmsItemId(id))
    dispatch(setLmsFormMode(lmsFormEditMode))
  }

  function handleBackClick() {
    dispatch(setLmsFormMode(lmsFormNoneMode))
  }

  function handleCreateButton() {
    dispatch(setLmsFormMode(lmsFormCreateMode))
  }

  const tableEditColumn = (params: any) => {
    return KeycloakService?.hasRole([LMS_ADMIN_WRITE]) ? (
      <a
        href='#'
        onClick={() => {
          onEdit(params?.data?.id)
        }}
      >
        {params?.data?.name}
      </a>
    ) : (
      <span>{params?.data?.name}</span>
    )
  }

  const handleArchival = async (data: any) => {
    const response = await enableDeviceModel(data)
    if (response?.success) {
      if (data?.enabled) {
        toast?.success(messages?.archiveSuccess)
      } else {
        toast?.success(messages?.unarchiveSuccess)
      }
      getDeviceModels(page, pageSize)
    } else {
      toast?.error(response?.message)
    }
  }

  const actionColumn = (params: any) => {
    return (
      <div>
        <IconButton
          title={params?.data?.enabled ? 'Archive' : 'Un-Archive'}
          size='small'
          onClick={() => handleArchival(params?.data)}
          disabled={
            !KeycloakService?.hasRole([SUPER_ADMIN_DELETE]) ||
            !(params?.data?.id === selectItem?.id)
          }
        >
          {params?.data?.enabled ? (
            <ArchiveOutlinedIcon fontSize='small' />
          ) : (
            <UnarchiveOutlined fontSize='small' />
          )}
        </IconButton>
        <IconButton
          title='Parameter Mappings'
          size='small'
          onClick={() =>
            navigate(`${NAVIGATE_PARAMETER_MAPPING}${params?.data?.id}`)
          }
          disabled={!(params?.data?.id === selectItem?.id)}
        >
          <AssignmentOutlinedIcon />
        </IconButton>
      </div>
    )
  }

  function handleCheckBox(data: any) {
    if (data?.id === selectItem?.id) {
      setSelectItem({})
    } else {
      setSelectItem(data)
    }
  }

  const columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'checkbox',
      minWidth: 50,
      sortable: false,
      filter: false,
      headerComponent: () => {
        return <input className='LisHeaderCheckboxStyle' type='checkbox' />
      },
      checkboxSelection: false,
      cellRenderer: (params: any) => {
        return (
          <input
            className='LisRowCheckboxStyle'
            type='checkbox'
            onChange={() => handleCheckBox(params?.data)}
            checked={params?.data?.id == selectItem?.id}
          />
        )
      },
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Id',
      field: 'id',
      minWidth: 50,
      sortable: true,
      filter: false,
      headerClass: 'custom-header-cell',
      cellClass: classes?.columnFontSize,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Device Model',
      field: 'name',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      cellRenderer: tableEditColumn,
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Equipment Name',
      field: 'equipment_name',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Department',
      field: 'department',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px' },
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Device Type',
      field: 'device_type',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
      resizable: false,
      pinned: 'left',
      lockPinned: true,
    },
    {
      headerName: 'Mapping Key',
      field: 'mapping_key',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Vendor',
      field: 'vendor',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Mode',
      field: 'default_connection_type',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Protocol',
      field: 'protocol',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Created By',
      field: 'created_by',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Created On',
      field: 'created_at',
      sortable: true,
      filter: 'agDateColumnFilter',
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      cellRenderer: (params: any) => {
        return readableDateTimeFormat(new Date(params?.data?.created_at))
      },
      minWidth: 250,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Updated By',
      field: 'updated_by',
      sortable: true,
      filter: true,
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      minWidth: 200,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Updated On',
      field: 'updated_at',
      sortable: true,
      filter: 'agDateColumnFilter',
      cellClass: classes?.columnFontSize,
      headerClass: 'custom-header-cell',
      cellRenderer: (params: any) => {
        return readableDateTimeFormat(new Date(params?.data?.updated_at))
      },
      minWidth: 250,
      filterParams: {
        buttons: ['clear'],
        closeOnApply: true,
      } as INumberFilterParams,
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Action',
      sortable: true,
      filter: false,
      cellClass: classes?.enableColumnStyle,
      headerClass: 'custom-header-cell',
      cellRenderer: actionColumn,
      width: 100,
    },
  ]

  const getDeviceModels = useCallback(
    async (page: number, pageSize: number) => {
      const response = await getAllLabDeviceModels(page * pageSize, pageSize)
      if (response?.success) {
        const data = response?.data?.data?.filter(
          (item: any) => item?.enabled === !archived
        )
        setData(data)
        setTotalCount(response?.data?.total_records)
      } else {
        setData([])
        toast?.error(response?.message)
      }
    },
    [archived]
  )

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen)
  }

  const handleStatusFilterChange = (filter: 'active' | 'inactive') => {
    if (filter === active) {
      setArchived(true)
    } else {
      setArchived(false)
    }
  }

  useEffect(() => {
    getDeviceModels(page, pageSize)
  }, [getDeviceModels, mode, page, pageSize, archived])

  return (
    <div className='LisFirstDivStyle'>
      <div className='LisSecondDivStyle'>
        <div className='admin-header-text LisThirdDivStyle'>
          <div>
            {mode !== lmsFormNoneMode && (
              <IconButton onClick={handleBackClick}>
                <KeyboardBackspaceIcon className='back-btn-lab' />
              </IconButton>
            )}
          </div>
          <span>
            {LAB_ANALYZER_TABLE_HEADING}&nbsp;{MODEL}
          </span>
        </div>
        {mode === lmsFormNoneMode && (
          <>
            <div style={{ position: 'relative' }}>
              <button className='admin-btn' onClick={toggleFilter}>
                Filter
              </button>
              {isFilterOpen && (
                <div className='dropdown-content'>
                  <div
                    className='menu2'
                    onClick={() => handleStatusFilterChange(activeText)}
                  >
                    Archived
                  </div>
                  <div
                    className='menu2'
                    onClick={() => handleStatusFilterChange(inactiveText)}
                  >
                    Un Archived
                  </div>
                </div>
              )}
            </div>
            <div className='LisSearchCreateButton'>
              <input
                className='admin-lab-searchbox LisSearchStyle'
                type='search'
                placeholder='Search'
              />
              <button className='admin-btn' onClick={handleCreateButton}>
                {CREATE}
              </button>
            </div>
          </>
        )}
      </div>
      {mode === lmsFormNoneMode ? (
        <div className='LisAgGridStyle' ref={depTableContainerRef}>
          <MedAgDataGrid
            tableHeight={'98%'}
            rowData={data?.length ? data : []}
            columnDefs={columnDefs}
            pagination={true}
            headerHeight={30}
            searchFieldWidth={200}
            searchField={false}
            exportIcon={false}
            pageSizeOptions={[10, 20, 50, 100]}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalRows={totalCount}
          />
        </div>
      ) : (
        <CreateUpdateLabDeviceModel />
      )}
    </div>
  )
}

export default LabDeviceModel
